import { useState, useEffect } from "react";
import DealVisorLogo from "../../assets/Images/Deal_visor.png";
import LockIcon from "@mui/icons-material/Lock";
import { useLocation, useNavigate } from "react-router-dom";
import { ResetPassword } from "../../Constants/apiRoutes";
import axios from "axios";
import PersonIcon from "@mui/icons-material/Person";
import { toast, ToastContainer } from "react-toastify";
import LoadingAnimation from "../../Components/Loading/LoadingAnimation";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import md5 from "md5";

const ResetPasswordPage = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState(location.state?.email || "");

  const features = [
    {
      icon: "📄",
      title: "Document Upload",
      description: "Securely upload and manage your documents",
    },
    {
      icon: "✍️",
      title: "Digital Signatures",
      description: "Sign documents electronically",
    },
    {
      icon: "👥",
      title: "Collaboration",
      description: "Review and approve documents together",
    },
    {
      icon: "🔒",
      title: "Secure Storage",
      description: "Keep your documents safe and organized",
    },
  ];

  useEffect(() => {
    setEmail(location.state?.email || "");
  }, [location.state?.email]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      setPasswordError("All fields are required");
      return;
    } else if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters long");
      return;
    } else if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    setPasswordError("");
    setIsLoading(true);

    try {
      const hashedNewPassword = md5(newPassword);
      const hashedConfirmPassword = md5(confirmPassword);

      const response = await axios.post(ResetPassword, {
        Email: email,
        NewPassword: hashedNewPassword,
        ConfirmPassword: hashedConfirmPassword,
      });

      if (
        response.status === 200 ||
        response.status === 201 ||
        response.StatusCode === "SUCCESS"
      ) {
        toast.success("Password reset successfully!");
        navigate("/");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      setPasswordError("Something went wrong. Please try again.");
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <ToastContainer />

      {/* Background Pattern with Gradient */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-white opacity-80" />

        {/* Animation Layer - Using GIF */}
        <div className="absolute inset-0 z-10 opacity-75 align-center">
          <DotLottieReact
            src="https://lottie.host/79fbfbdd-bf0f-4478-9136-b101cd217fe8/T7NlAlJBhK.lottie"
            loop={true}
            autoplay={true}
            className="absolute inset-0 h-full w-full object-contain shadow-sm"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-20 flex flex-col lg:flex-row w-full min-h-screen">
        {/* Left Side - Now visible on all screens */}
        <div className="w-full lg:w-1/2 p-4 sm:p-8 lg:p-12">
          <div className="flex items-center gap-10">
            {/* Logo */}
            <img
              src={DealVisorLogo}
              alt="DealVisorLogo"
              className="h-28 w-auto"
            />

            {/* Heading */}
            <h1 className="text-3xl sm:text-4xl text-[#8B4513] lg:text-5xl font-bold mb-4">
              <span className="block">Reset Password</span>
            </h1>
          </div>
          <div className="text-[#8B4513] max-w-xl mx-auto lg:mx-0 mt-8 lg:mt-20 text-center lg:text-left mb-8 lg:mb-0">
            {/* Features Grid - Updated background and text colors */}
            <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-6 mt-40">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 bg-[#8B4513] rounded-lg p-4 transition-all duration-300 backdrop-blur-sm 
      border border-[#8B4513]" // Add border here
                >
                  <div className="text-white text-2xl shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-white text-sm">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8">
          <div className="bg-[#8B4513] rounded-3xl p-6 sm:p-8 w-full max-w-[400px] shadow-xl backdrop-blur-lg">
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    readOnly
                    className="w-full px-4 py-3 pl-12 rounded-xl border-[0.5px] border-gray-200 bg-gray-50 focus:border-[#8B4513] focus:ring-2 focus:ring-[#8B4513]/20 outline-none transition-all duration-300"
                  />
                  <PersonIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8B4513]" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 pl-12 rounded-xl border-[0.5px] border-gray-200 focus:border-[#8B4513] focus:ring-2 focus:ring-[#8B4513]/20 outline-none transition-all duration-300"
                    placeholder="Enter new password"
                  />
                  <LockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8B4513]" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 pl-12 rounded-xl border-[0.5px] border-gray-200 focus:border-[#8B4513] focus:ring-2 focus:ring-[#8B4513]/20 outline-none transition-all duration-300"
                    placeholder="Confirm new password"
                  />
                  <LockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8B4513]" />
                </div>
              </div>

              {passwordError && (
                <p className="text-red-500 text-sm animate-pulse text-center">
                  {passwordError}
                </p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-white text-[#8B4513] rounded-xl font-semibold transition-all duration-300 disabled:opacity-50"
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {isLoading && <LoadingAnimation />}
    </div>
  );
};

export default ResetPasswordPage;
